import { GlobalPreferencesService } from 'business/services/GlobalPreferencesService';
import SwitchButton from 'components/customComponents/Controls/SwitchButton';
import { RMColorPicker } from 'components/main/GlobalPrefrences/GlobalPrefrences';
import { RMInput } from 'components/main/ProteinFormulation/RMInput';
import RMSelect from 'components/customComponents/Controls/RMSelect';
import React, { useEffect, useMemo, useState } from 'react';
import cloneDeep from 'lodash/cloneDeep';
import { IStyle } from 'Styles';
import getExperimentInfoDetails from '../ExperimentInfo/ExperimentInfo';
import { isNullOrUndefined } from 'utils/Global';
import {
  setDispensingRobotId,
  setExperiment,
} from 'redux/reducers/experimentDataReducer';
import { useRMDispatch, useRMSelector } from 'redux/hooks';
import { selectExp } from 'redux/selectors/ExperimentStateSelector';
import { setExpInfoIsDirty } from '../ImagingSchedule/ImagingScheduleData';
import {
  BaseDriverProperty,
  DBListProperty,
  ExperimentDispensingProperties,
  ListEnum,
  ListProperty,
  Property,
  PropertyType,
  StringProperty,
} from 'components/Experiment/constants';

export default function Dispensing() {
  const [dispensingProperties, setDispensingProperties] =
    useState<ExperimentDispensingProperties>(null);
  const [tempDispensingProps, setTempDispensingProps] =
    useState<ExperimentDispensingProperties>(null);
  const expInfo = getExperimentInfoDetails();
  const exp = useRMSelector(selectExp);
  const [selectedRobotId, setSelectedRobotId] = useState(exp?.info?.dispensingRobotId);
  const dispatch = useRMDispatch();
  useEffect(() => {
    // Handle Discard changes
    if (!exp.dispensingProps && dispensingProperties) {
      setTempDispensingProps(
        cloneDeep({ ...dispensingProperties }) as ExperimentDispensingProperties,
      );
    }
    // Ignore unnecessary renders
    if (isNullOrUndefined(exp.info) || !isNullOrUndefined(dispensingProperties)) {
      return;
    }
    fetchDispensingProperties();
  }, [exp]);

  const fetchDispensingProperties = async () => {
    const res = await new GlobalPreferencesService().getDispensingProperties(
      exp.info.containerId,
      exp.experimentId,
    );
    if (exp.dispensingProps?.length) {
      exp.dispensingProps.forEach((prop) => {
        const propertyIndex = res.properties[prop.dispensingRobotId].findIndex(
          (p) => p.name === prop.property.name,
        );
        res.properties[prop.dispensingRobotId][propertyIndex] = prop.property;
      });
    }
    setDispensingProperties(res);
    setTempDispensingProps(cloneDeep({ ...res }) as ExperimentDispensingProperties);
    setSelectedRobotId(exp?.info?.dispensingRobotId);
  };

  const robots = useMemo(() => {
    if (dispensingProperties == null) {
      return [];
    }
    const arr = [];
    dispensingProperties.robots.map((robot) => {
      arr.push(<option value={robot.id}>{robot.name}</option>);
    });
    return arr;
  }, [dispensingProperties]);

  const robotSelectionChanged = (e) => {
    const robotId = parseInt(e.target.value);
    setSelectedRobotId(robotId);
    dispatch(setDispensingRobotId(robotId));
    setExpInfoIsDirty(true);
  };

  const onChangeProp = (prop: Property, e: any) => {
    const robotProperties = tempDispensingProps.properties
      ? [...(tempDispensingProps.properties[selectedRobotId] || [])]
      : [];
    const dispensingProps = exp.dispensingProps ? [...exp.dispensingProps] : [];

    const updatedProp = { ...prop };

    if (prop.type === PropertyType.ListEnum) {
      const val = (prop as ListEnum).options.find(
        (opt) => opt.id === parseInt(e.target.value),
      );
      updatedProp.value = val;
    } else if (
      prop.type === PropertyType.DBListProperty ||
      prop.type === PropertyType.ListProperty
    ) {
      updatedProp.value = (prop as DBListProperty | ListProperty).options.find(
        (opt) => opt.name === e.target.value,
      );
    } else if (prop.type === PropertyType.ColorProperty) {
      updatedProp.value = e;
    } else if (
      prop.type === PropertyType.IntProperty ||
      prop.type === PropertyType.LongProperty
    ) {
      updatedProp.value = parseInt(e.target.value);
    } else if (
      prop.type === PropertyType.DoubleProperty ||
      prop.type === PropertyType.FloatProperty
    ) {
      updatedProp.value = e.target.value;
    } else if (prop.type === PropertyType.StringProperty) {
      updatedProp.value = e.target.value;
    } else if (prop.type === PropertyType.BooleanProperty) {
      updatedProp.value = e;
    }

    const idx = dispensingProps.findIndex((p) => p.property.name === prop.name);

    const baseDriverProp: BaseDriverProperty = {
      dispensingRobotId: selectedRobotId,
      property: updatedProp,
    };

    if (idx >= 0) {
      dispensingProps[idx] = baseDriverProp;
    } else {
      dispensingProps.push(baseDriverProp);
    }

    const propIndex = robotProperties.findIndex((p) => p.name === prop.name);
    if (propIndex >= 0) {
      robotProperties[propIndex] = updatedProp;
    } else {
      robotProperties.push(updatedProp);
    }

    const updatedProperties = {
      ...tempDispensingProps.properties,
      [selectedRobotId]: robotProperties,
    };
    expInfo.IsDirty = true;
    dispatch(setExperiment({ dispensingProps: dispensingProps }));
    setTempDispensingProps({
      ...tempDispensingProps,
      properties: updatedProperties,
    } as ExperimentDispensingProperties);
  };

  const activeProps = useMemo(() => {
    if (tempDispensingProps == null) {
      return [];
    }
    const props = tempDispensingProps.properties[selectedRobotId]
      ? tempDispensingProps.properties[selectedRobotId]
      : [];
    const propsView = [];
    let inputTag;
    props.forEach((prop) => {
      if (
        prop.type === PropertyType.DBListProperty ||
        prop.type === PropertyType.ListProperty
      ) {
        const listProp = prop as DBListProperty | ListProperty;
        inputTag = (
          <RMSelect
            key={listProp.name}
            id={`select-${listProp.name}`}
            className="flex-50"
            style={{ heigth: '2rem' }}
            options={listProp.options.map((opt) => {
              return { id: parseInt(opt.name), name: opt.displayName };
            })}
            selectedValue={parseInt(listProp.value.name)}
            onChange={(e) => onChangeProp(listProp, e)}
            placeholder={'any'}
            disabled={false}
          />
        );
      } else if (prop.type === PropertyType.ListEnum) {
        const listProp = prop as ListEnum;
        inputTag = (
          <RMSelect
            key={listProp.name}
            id={`select-${listProp.name}`}
            className="flex-50"
            style={{ heigth: '2rem' }}
            options={listProp.options.map((opt) => {
              return { id: opt.id, name: opt.displayName };
            })}
            selectedValue={listProp.value.id}
            onChange={(e) => onChangeProp(listProp, e)}
            placeholder={'any'}
            disabled={false}
          />
        );
      } else if (prop.type === PropertyType.StringProperty) {
        const stringProp = prop as StringProperty;
        inputTag = (
          <input
            key={stringProp.name}
            className="inputFieldDetails flex-50"
            type="text"
            onChange={(e) => {
              onChangeProp(stringProp, e);
            }}
            value={stringProp.value}
          />
        );
      } else if (
        prop.type === PropertyType.IntProperty ||
        prop.type === PropertyType.DoubleProperty ||
        prop.type === PropertyType.LongProperty ||
        prop.type === PropertyType.FloatProperty
      ) {
        inputTag = (
          <RMInput
            key={prop.name}
            id={`input-${prop.name}`}
            className="inputFieldDetails flex-50"
            onChange={(e) => {
              onChangeProp(prop, e);
            }}
            value={prop.value}
            allowDecimals={
              prop.type === PropertyType.DoubleProperty ||
              prop.type === PropertyType.FloatProperty
            }
          />
        );
      } else if (prop.type === PropertyType.BooleanProperty) {
        inputTag = (
          <div className="flex-50">
            <SwitchButton
              key={prop.name}
              onToggle={(e) => {
                onChangeProp(prop, e);
              }}
              isOn={prop.value}
            />
          </div>
        );
      } else if (prop.type === PropertyType.ColorProperty) {
        inputTag = (
          <RMColorPicker
            className="flex-50"
            key={prop.name}
            color={prop.value}
            onChange={(e) => onChangeProp(prop, e)}
          />
        );
      }
      propsView.push(
        <div key={prop.name + '_div'} style={styles.propertyRow}>
          <label key={prop.name + '_label'} className="text-label flex-50">
            {prop.displayName}
          </label>
          {inputTag}
        </div>,
      );
    });
    return propsView;
  }, [selectedRobotId, tempDispensingProps]);

  return (
    <>
      <div id="dispensing-container" style={styles.root}>
        Dispensing
      </div>
      <div id="dispensing-main-wrapper" className="w-full">
        <div id="dispensing-device-row" className="flex flex-row w-full mt-3 ml-2">
          <div id="dispensing-device-header" style={styles.deviceLabel}>
            Dispensing Device
          </div>
          <div id="dispensing-device-select-wrapper" style={styles.deviceLabel}>
            <select
              data-testid="select-robot"
              name="scheduling"
              id="dispensing-device-select"
              style={{ float: 'right', width: '100%', padding: 5 }}
              value={selectedRobotId}
              onChange={robotSelectionChanged}
            >
              {robots}
            </select>
          </div>
        </div>
        <div
          id="dispensing-properties-header"
          style={styles.propertyHeader}
          className="flex flex-row mt-2"
        >
          <div id="dispensing-propname-label" style={styles.propertyLabel}>
            Name
          </div>
          <div id="dispensing-propvalue-label" style={styles.propertyLabel}>
            Value
          </div>
        </div>
        <div
          id="dispensing-properties-container"
          style={styles.propertyContainer}
          className="flex flex-col ml-1 p-1"
        >
          {activeProps}
        </div>
      </div>
    </>
  );
}

const styles: IStyle = {
  root: {
    fontWeight: 700,
    fontFamily: 'Arial',
    color: '#F7A246',
    padding: '15px 10px',
    background: '#E4E4E4',
  },
  propertyLabel: { flex: '50%', textAlign: 'center', fontWeight: 700 },
  propertyContainer: {
    height: '85%',
    width: '99%',
    overflowY: 'auto',
    gap: '0.5rem',
    flexDirection: 'column',
  },
  propertyHeader: {
    height: '5%',
    width: '100%',
    overflowY: 'auto',
    gap: '0.5rem',
    flexDirection: 'row',
    background: '#D9D9D9',
    alignItems: 'center',
  },
  deviceLabel: {
    fontWeight: 400,
    fontSize: 13,
    fontFamily: 'Arial',
    padding: 0,
    width: '49%',
  },
  propertyRow: { display: 'flex', flexWrap: 'wrap', alignItems: 'baseline' },
};
